import {
  ViveApiBaseService,
  ViveApiRequestOptions
} from './vive-api-base.service';
import {
  ApVendorMetaCreateRequestDto,
  ApVendorMetaDto,
  CompaniesApVendorMetaBulkRequestDto,
  CompaniesApVendorMetaBulkResponseDto,
  CompaniesClientInfoUpdateRequestDto,
  CompaniesCommunicationsFindAllRequestDto,
  CompaniesCommunicationsFindAllResponseDto,
  CompaniesCommunicationsFindOneResponseDto,
  CompaniesFindAllRequestDto,
  CompaniesFindAllResponseDto,
  CompaniesFindVendorDirectoryRequestDto,
  CompaniesFindVendorDirectoryResponseDto,
  CompaniesInviteFromVendorDirectoryRequestDto,
  CompaniesInviteFromVendorDirectoryResponseDto,
  CompaniesVendorsFindAllRequestDto,
  CompanyByInviteCodeResponseDto,
  CompanyClientInfoDto,
  CompanyDto,
  ProposalRequestDto,
  ProposalRequestsCreateRequestDto,
  ProposalRequestsFindAllRequestDto,
  ProposalRequestsFindAllResponseDto,
  ProposalRequestsUpdateOneRequestDto,
  UsersFindAllForCompaniesRequestDto,
  UsersFindAllResponseDto
} from './vive-api-models';

class CompaniesService extends ViveApiBaseService {
  public async findAll(params: {
    options?: ViveApiRequestOptions;
    queryParams?: Omit<CompaniesFindAllRequestDto, 'includeAssociations'>;
  }): Promise<CompaniesFindAllResponseDto> {
    const { options, queryParams } = params;
    return await this.sendGet<CompaniesFindAllResponseDto>({
      apiEndpoint: 'companies',
      options,
      queryParams
    });
  }

  public async findOneById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams?: Pick<CompaniesFindAllRequestDto, 'includeAssociations'>;
  }): Promise<CompanyDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyDto>({
      apiEndpoint: 'companies/:companyId',
      options,
      pathParams,
      queryParams
    });
  }

  public async findVendorDirectory(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams?: Omit<
      CompaniesFindVendorDirectoryRequestDto,
      'includeAssociations'
    >;
  }): Promise<CompaniesFindVendorDirectoryResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompaniesFindVendorDirectoryResponseDto>({
      apiEndpoint: 'companies/:companyId/vendor-directory',
      options,
      pathParams,
      queryParams
    });
  }

  public async inviteFromVendorDirectory(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    data: CompaniesInviteFromVendorDirectoryRequestDto;
  }): Promise<CompaniesInviteFromVendorDirectoryResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompaniesInviteFromVendorDirectoryResponseDto>({
      apiEndpoint: 'companies/:companyId/vendor-directory/invite',
      options,
      pathParams,
      data
    });
  }

  public async findAllCommunications(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams?: Omit<
      CompaniesCommunicationsFindAllRequestDto,
      'includeAssociations'
    >;
  }): Promise<CompaniesCommunicationsFindAllResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompaniesCommunicationsFindAllResponseDto>({
      apiEndpoint: 'companies/:companyId/communications',
      options,
      pathParams,
      queryParams
    });
  }

  public async findOneCommunication(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; emailId: number };
  }): Promise<CompaniesCommunicationsFindOneResponseDto> {
    const { options, pathParams } = params;
    return await this.sendGet<CompaniesCommunicationsFindOneResponseDto>({
      apiEndpoint: 'companies/:companyId/communications/:emailId',
      options,
      pathParams
    });
  }

  public async findClientInfo(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
  }): Promise<CompanyClientInfoDto> {
    const { options, pathParams } = params;
    return await this.sendGet<CompanyClientInfoDto>({
      apiEndpoint: 'companies/:companyId/client-info',
      options,
      pathParams
    });
  }

  public async updateClientInfo(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    data: CompaniesClientInfoUpdateRequestDto;
  }): Promise<CompanyClientInfoDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<CompanyClientInfoDto>({
      apiEndpoint: 'companies/:companyId/client-info',
      options,
      pathParams,
      data
    });
  }

  public async findAllUsersForCompany(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams?: UsersFindAllForCompaniesRequestDto;
  }): Promise<UsersFindAllResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<UsersFindAllResponseDto>({
      apiEndpoint: 'companies/:companyId/users',
      options,
      pathParams,
      queryParams: queryParams && { ...queryParams }
    });
  }

  public async findByInviteCode(params: {
    pathParams: { inviteCode: string };
  }): Promise<CompanyByInviteCodeResponseDto> {
    const { pathParams } = params;
    return await this.sendGet<CompanyByInviteCodeResponseDto>({
      apiEndpoint: 'companies/invite/:inviteCode',
      pathParams,
      authRequired: false
    });
  }

  public async findOneForVendors(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams?: Pick<
      CompaniesVendorsFindAllRequestDto,
      'includeAssociations'
    >;
  }): Promise<CompanyDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyDto>({
      apiEndpoint: 'companies/vendors/:companyId',
      options,
      pathParams,
      queryParams
    });
  }

  public async findOneAvailableForVendors(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams?: Pick<
      CompaniesVendorsFindAllRequestDto,
      'includeAssociations'
    >;
  }): Promise<CompanyDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<CompanyDto>({
      apiEndpoint: 'companies/vendors/available/:companyId',
      options,
      pathParams,
      queryParams
    });
  }

  public async findAllForVendors(params: {
    options?: ViveApiRequestOptions;
    queryParams?: Omit<
      CompaniesVendorsFindAllRequestDto,
      'includeAssociations'
    >;
  }): Promise<CompaniesFindAllResponseDto> {
    const { options, queryParams } = params;
    return await this.sendGet<CompaniesFindAllResponseDto>({
      apiEndpoint: 'companies/vendors',
      options,
      queryParams
    });
  }

  public async findAllAvailableForVendors(params: {
    options?: ViveApiRequestOptions;
    queryParams?: Omit<
      CompaniesVendorsFindAllRequestDto,
      'includeAssociations'
    >;
  }): Promise<CompaniesFindAllResponseDto> {
    const { options, queryParams } = params;
    return await this.sendGet<CompaniesFindAllResponseDto>({
      apiEndpoint: 'companies/vendors/available',
      options,
      queryParams
    });
  }

  public async findBySignUpCode(params: {
    pathParams: { signUpCode: string };
  }): Promise<CompanyByInviteCodeResponseDto> {
    const { pathParams } = params;
    return await this.sendGet<CompanyByInviteCodeResponseDto>({
      apiEndpoint: 'companies/signup/:signUpCode',
      pathParams,
      authRequired: false
    });
  }

  public async findAllProposalRequests(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    queryParams?: Omit<
      ProposalRequestsFindAllRequestDto,
      'includeAssociations'
    >;
  }): Promise<ProposalRequestsFindAllResponseDto> {
    const { options, pathParams, queryParams } = params;
    return await this.sendGet<ProposalRequestsFindAllResponseDto>({
      apiEndpoint: 'companies/:companyId/proposal-requests',
      options,
      pathParams,
      queryParams
    });
  }

  public async findOneProposalRequestById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; proposalRequestId: number };
  }): Promise<ProposalRequestDto> {
    const { options, pathParams } = params;
    return await this.sendGet<ProposalRequestDto>({
      apiEndpoint: 'companies/:companyId/proposal-requests/:proposalRequestId',
      options,
      pathParams
    });
  }

  public async createProposalRequest(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    data: ProposalRequestsCreateRequestDto;
  }): Promise<ProposalRequestDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<ProposalRequestDto>({
      apiEndpoint: 'companies/:companyId/proposal-requests',
      options,
      pathParams,
      data
    });
  }

  public async updateOneProposalRequestById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; proposalRequestId: number };
    data: ProposalRequestsUpdateOneRequestDto;
  }): Promise<ProposalRequestDto> {
    const { options, pathParams, data } = params;
    return await this.sendPatch<ProposalRequestDto>({
      apiEndpoint: 'companies/:companyId/proposal-requests/:proposalRequestId',
      options,
      pathParams,
      data
    });
  }

  public async deleteOneProposalRequestById(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; proposalRequestId: number };
  }): Promise<void> {
    const { options, pathParams } = params;
    return await this.sendDelete<void>({
      apiEndpoint: 'companies/:companyId/proposal-requests/:proposalRequestId',
      options,
      pathParams
    });
  }

  public async createApVendorMeta(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number };
    data: ApVendorMetaCreateRequestDto;
  }): Promise<ApVendorMetaDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<ApVendorMetaDto>({
      apiEndpoint: 'companies/:companyId/ap-vendor-meta',
      options,
      pathParams,
      data
    });
  }

  public async bulkCreateApVendorMeta(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; apConfigId: number };
    data: CompaniesApVendorMetaBulkRequestDto;
  }): Promise<CompaniesApVendorMetaBulkResponseDto> {
    const { options, pathParams, data } = params;
    return await this.sendPost<CompaniesApVendorMetaBulkResponseDto>({
      apiEndpoint:
        'companies/:companyId/ap-configs/:apConfigId/ap-vendor-meta/bulk',
      options,
      pathParams,
      data
    });
  }

  public async deleteApVendorMetaByApConfigIdAndVendorKey(params: {
    options?: ViveApiRequestOptions;
    pathParams: { companyId: number; apConfigId: number; vendorKey: string };
  }): Promise<number> {
    const { options, pathParams } = params;
    return await this.sendDelete<number>({
      apiEndpoint:
        'companies/:companyId/ap-vendor-meta/ap-configs/:apConfigId/ap-vendors/:vendorKey',
      options,
      pathParams
    });
  }
}

export const companiesService = new CompaniesService();
